import React from 'react';
import {
  Box, Table, TableBody, TableCell, TableHead, TableRow, Typography, IconButton, Tooltip,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import {
  CardWrapper, SectionTitle, ButtonUI, ButtonOutlined, Field, formatBRL,
} from '../../UI';
import { CALCULO_PROCESSO, ANTECEDENTES, TITULOS_PROCESSO } from '../../../mocks/processos.mock';
import * as C from '../../../theme/colors.constants';

/**
 * Aba Acompanhamento.
 *
 * Três blocos, como no print `grid_icons_check.png`:
 *   1. Cálculo do Processo — campos com os sinais (+)/(-)/(=) à direita
 *   2. Processos Antecedentes — tabela + total
 *   3. Títulos do processo — tabela larga, abaixo dos dois primeiros
 */
const LINHAS_CALCULO = [
  { id: 'principal', label: 'Principal:', sinal: '(+)' },
  { id: 'protesto', label: 'Protesto:', sinal: '(+)' },
  { id: 'multa', label: 'Multa:', sinal: '(+)' },
  {
    id: 'subtotal', label: 'SUBTOTAL:', sinal: '(=)', destaque: true, moeda: true,
  },
  { id: 'jurosDesp', label: 'Juros/Desp:', sinal: '(+)' },
  { id: 'honorarios', label: 'Honorários:', sinal: '(+)' },
  { id: 'desconto', label: 'Desconto:', sinal: '(-)' },
  {
    id: 'total', label: 'TOTAL:', sinal: '', destaque: true, moeda: true,
  },
  {
    id: 'renegociado', label: 'RENEGOCIADO:', sinal: '', destaque: true, moeda: true,
  },
];

export default function AbaAcompanhamento() {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'clamp(10px, 0.8vw, 22px)' }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 0.85fr) minmax(0, 1.15fr)' },
          gap: 'clamp(10px, 0.8vw, 22px)',
          alignItems: 'start',
        }}
      >
        {/* --- Cálculo do Processo --- */}
        <CardWrapper>
          <SectionTitle>Cálculo do Processo</SectionTitle>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {LINHAS_CALCULO.map(l => (
              <Box key={l.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  sx={{
                    width: '38%',
                    flexShrink: 0,
                    fontSize: 'clamp(12px, 0.7vw, 16px)',
                    fontWeight: l.destaque ? 700 : 400,
                    color: C.black,
                  }}
                >
                  {l.label}
                </Typography>
                <Field
                  tipo="texto"
                  value={l.moeda ? formatBRL(CALCULO_PROCESSO[l.id]) : String(CALCULO_PROCESSO[l.id] ?? '')}
                  InputProps={{ readOnly: true }}
                  sx={{ flex: 1 }}
                />
                <Typography variant="caption" sx={{ width: 22, color: C.warmGrey, flexShrink: 0 }}>
                  {l.sinal}
                </Typography>
              </Box>
            ))}
          </Box>
          <ButtonUI startIcon={<HandshakeOutlinedIcon />} sx={{ mt: 2 }}>
            ACORDO
          </ButtonUI>
        </CardWrapper>

        {/* --- Processos Antecedentes --- */}
        <CardWrapper>
          <SectionTitle
            right={(
              <Tooltip title="Configurar colunas" arrow>
                <IconButton size="small" sx={{ color: C.navy }}><TuneIcon fontSize="small" /></IconButton>
              </Tooltip>
            )}
          >
            Processos Antecedentes
          </SectionTitle>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Número</TableCell>
                <TableCell>Responsável</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ANTECEDENTES.map(a => (
                <TableRow key={a.numero} hover>
                  <TableCell sx={{ color: C.navy, fontWeight: 600 }}>{a.numero}</TableCell>
                  <TableCell>{a.responsavel}</TableCell>
                  <TableCell>{a.status}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Typography sx={{ fontWeight: 700, mt: 1.5 }}>
            Total de Antecedentes:
            {' '}
            {ANTECEDENTES.length}
          </Typography>
        </CardWrapper>
      </Box>

      {/* --- Títulos do processo --- */}
      <CardWrapper>
        <SectionTitle
          right={(
            <Box sx={{ display: 'flex', gap: 1 }}>
              <ButtonOutlined size="small">DEMONSTRATIVO DE VALORES</ButtonOutlined>
              <ButtonOutlined size="small">EXPANDIR TÍTULOS</ButtonOutlined>
            </Box>
          )}
        >
          Títulos do processo
        </SectionTitle>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Seq.</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Número</TableCell>
                <TableCell>Vencimento</TableCell>
                <TableCell align="center">Atraso (dias)</TableCell>
                <TableCell align="right">Valor Original</TableCell>
                <TableCell align="right">Saldo</TableCell>
                <TableCell align="right">Protesto</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {TITULOS_PROCESSO.map(t => (
                <TableRow key={`${t.seq}-${t.numero}`} hover>
                  <TableCell>{t.seq}</TableCell>
                  <TableCell>{t.tipo}</TableCell>
                  <TableCell sx={{ color: C.navy, fontWeight: 600 }}>{t.numero}</TableCell>
                  <TableCell>{t.vencimento}</TableCell>
                  <TableCell align="center" sx={{ color: t.atraso > 90 ? C.tomato : C.black, fontWeight: 600 }}>
                    {t.atraso}
                  </TableCell>
                  <TableCell align="right">{formatBRL(t.valorOriginal)}</TableCell>
                  <TableCell align="right">{formatBRL(t.saldo)}</TableCell>
                  <TableCell align="right">{formatBRL(t.protesto)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </CardWrapper>
    </Box>
  );
}
